import { useState, useEffect, useCallback } from 'react';
import { doc, onSnapshot, setDoc, Timestamp, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import type { WebsiteOnboarding, OnboardingStatus } from '../lib/db/types';
import type { OnboardingFormData } from '../lib/validations/onboarding';

export interface JsonOutputs {
  generalJson: string;
  pricingJson: string;
  scheduleJson: string;
}

export interface OnboardingData {
  data: OnboardingFormData | null;
  status: OnboardingStatus;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
  submittedAt?: Timestamp;
  reviewedAt?: Timestamp;
}

export interface UseCustomerOnboardingReturn {
  onboarding: OnboardingData | null;
  loading: boolean;
  error: Error | null;
  saveOnboarding: (data: OnboardingFormData, status?: OnboardingStatus) => Promise<void>;
  generateJsonOutputs: (data: OnboardingFormData) => JsonOutputs;
}

export function useCustomerOnboarding(customerId: string): UseCustomerOnboardingReturn {
  const [onboarding, setOnboarding] = useState<OnboardingData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  // Subscribe to onboarding document changes
  useEffect(() => {
    if (!customerId) {
      setOnboarding(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const onboardingDocRef = doc(db, 'customers', customerId, 'website', 'onboarding');

    const unsubscribe = onSnapshot(
      onboardingDocRef,
      (snapshot) => {
        setLoading(false);
        if (snapshot.exists()) {
          const data = snapshot.data() as WebsiteOnboarding;
          setOnboarding({
            data: data as unknown as OnboardingFormData,
            status: data.status || 'draft',
            createdAt: data.createdAt,
            updatedAt: data.updatedAt,
            submittedAt: data.submittedAt,
            reviewedAt: data.reviewedAt,
          });
        } else {
          // Document doesn't exist yet - user hasn't started onboarding
          setOnboarding(null);
        }
      },
      (err) => {
        console.error('Error fetching onboarding data:', err);
        setError(err instanceof Error ? err : new Error(String(err)));
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [customerId]);

  // Save onboarding data with merge option
  const saveOnboarding = useCallback(
    async (formData: OnboardingFormData, status: OnboardingStatus = 'draft') => {
      if (!customerId) {
        throw new Error('Customer ID is required');
      }

      const onboardingDocRef = doc(db, 'customers', customerId, 'website', 'onboarding');
      const now = Timestamp.now();

      // Check if document exists to determine if we need to set createdAt
      const existingDoc = await getDoc(onboardingDocRef);
      const isNew = !existingDoc.exists();

      const saveData: Partial<WebsiteOnboarding> = {
        ...formData,
        status,
        updatedAt: now,
      };

      // Only set createdAt on first save
      if (isNew) {
        saveData.createdAt = now;
      }

      // Set submittedAt when status changes from draft to pending/completed
      if (status !== 'draft' && (!existingDoc.exists() || existingDoc.data()?.status === 'draft')) {
        saveData.submittedAt = now;
      }

      await setDoc(onboardingDocRef, saveData, { merge: true });
    },
    [customerId]
  );

  // Generate JSON outputs for website integration
  const generateJsonOutputs = useCallback((data: OnboardingFormData): JsonOutputs => {
    // Generate general info JSON
    const generalData = {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      name: data.name,
      alternateName: data.shortName,
      description: data.description,
      url: data.url,
      logo: data.logo,
      image: data.image,
      telephone: data.telephone,
      email: data.email,
      address: {
        '@type': 'PostalAddress',
        streetAddress: data.streetAddress,
        addressLocality: data.addressLocality,
        addressRegion: data.addressRegion,
        postalCode: data.postalCode,
        addressCountry: data.addressCountry,
      },
      geo: data.latitude && data.longitude ? {
        '@type': 'GeoCoordinates',
        latitude: data.latitude,
        longitude: data.longitude,
      } : undefined,
      sameAs: [
        data.instagram,
        data.facebook,
        data.youtube,
        data.patreon,
      ].filter(Boolean),
      openingHours: data.openingHours?.map((oh) => ({
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: oh.dayOfWeek,
        opens: oh.opens,
        closes: oh.closes,
      })),
    };

    // Generate pricing JSON
    const pricingData = {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      itemListElement: data.plans.map((plan, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'Offer',
          name: plan.name,
          description: plan.description,
          price: plan.price.toString(),
          priceCurrency: 'USD',
          billingIncrement: plan.billing,
          url: plan.checkoutLink || undefined,
          isPopular: plan.popular,
          features: plan.features,
        },
      })),
    };

    // Generate schedule JSON
    const scheduleData = {
      '@context': 'https://schema.org',
      '@type': 'Schedule',
      byDay: data.schedule.map((item) => ({
        '@type': 'Schedule',
        byDay: item.day,
        startTime: item.time,
        name: item.title,
        description: `Level: ${item.level}, Type: ${item.type}, Age Group: ${item.ageGroup}`,
      })),
    };

    return {
      generalJson: JSON.stringify(generalData, null, 2),
      pricingJson: JSON.stringify(pricingData, null, 2),
      scheduleJson: JSON.stringify(scheduleData, null, 2),
    };
  }, []);

  return {
    onboarding,
    loading,
    error,
    saveOnboarding,
    generateJsonOutputs,
  };
}

export default useCustomerOnboarding;
